/**
 * B18 — MLEADER grip context menu (right-click on MLEADER grip).
 *
 * AutoCAD parity: right-clicking on a multileader's grip pops a small
 * context menu with the leader-edit operations:
 *   - Add Leader      → append a new leader line; user picks new arrow.
 *   - Remove Leader   → delete the leader line owning this grip.
 *   - Add Vertex      → insert an intermediate vertex on the leader
 *                        between the current grip and the next vertex.
 *   - Remove Vertex   → delete the vertex this grip references (only
 *                        valid for `mleader-vertex` grips on a leader
 *                        line with ≥ 3 vertices).
 *   - Stretch         → close the menu and let the user drag the grip.
 *
 * The host (App.tsx) owns the dispatch logic and dialog state; this
 * component is purely presentational (one menu, one click → callback).
 *
 * Wiring:
 *   1. `<GripsDomLayer onGripContextMenu={...}>` invokes the host with
 *      `{ entityId, gripKind, gripPayload, gripWorld, screen }`.
 *   2. Host stores the descriptor + mounts `<MleaderGripContextMenu>`.
 *   3. Menu item click → callback prop on the host → reducer dispatch.
 *   4. ESC / outside-click → onCancel → menu unmounts.
 */

import * as React from "react";

import { useT } from "../../i18n";

/**
 * Context-menu action identifier. The host maps each id to the
 * appropriate reducer command (see App.tsx mleaderMenuPick callback).
 */
export type MleaderMenuActionId =
  | "add-leader"
  | "remove-leader"
  | "add-vertex"
  | "remove-vertex"
  | "stretch";

/**
 * Grip kinds the menu cares about (mleader-* family). The action set
 * is filtered per kind: `Remove Vertex` only appears on
 * `mleader-vertex`; `Add Vertex` is restricted to vertex-bearing kinds
 * (`mleader-vertex` / `mleader-arrow` / `mleader-landing`); `Stretch`
 * always appears so the user can dismiss the menu and drag.
 */
export type MleaderGripKind =
  | "mleader-arrow"
  | "mleader-vertex"
  | "mleader-landing"
  | "mleader-dogleg"
  | "mleader-text";

export interface MleaderGripContextMenuProps {
  /** Screen-space anchor (CSS px relative to document). */
  readonly screen: { readonly x: number; readonly y: number };
  /** Which grip kind was right-clicked — drives the menu items shown. */
  readonly gripKind: MleaderGripKind;
  /** Invoked when the user clicks a menu item. */
  readonly onPick: (action: MleaderMenuActionId) => void;
  /** Invoked on Esc / outside-click / Cancel button. */
  readonly onCancel: () => void;
}

interface MenuEntry {
  readonly id: MleaderMenuActionId;
  readonly labelKey:
    | "mleader.menu.add_leader"
    | "mleader.menu.remove_leader"
    | "mleader.menu.add_vertex"
    | "mleader.menu.remove_vertex"
    | "mleader.menu.stretch";
}

/**
 * Resolve which menu items appear for the given grip kind. AutoCAD
 * shows different sets per kind — we mirror that.
 */
export function resolveMenuEntries(kind: MleaderGripKind): MenuEntry[] {
  const entries: MenuEntry[] = [];
  // Add Leader is always available (new leader appended to first node).
  entries.push({ id: "add-leader", labelKey: "mleader.menu.add_leader" });
  // Remove Leader is valid for any vertex-bearing grip on a leader line.
  if (
    kind === "mleader-arrow" ||
    kind === "mleader-vertex" ||
    kind === "mleader-landing"
  ) {
    entries.push({
      id: "remove-leader",
      labelKey: "mleader.menu.remove_leader",
    });
  }
  // Add Vertex — append after the grip's vertex (valid on the entire
  // leader line, not on text/dogleg grips).
  if (
    kind === "mleader-arrow" ||
    kind === "mleader-vertex" ||
    kind === "mleader-landing"
  ) {
    entries.push({
      id: "add-vertex",
      labelKey: "mleader.menu.add_vertex",
    });
  }
  // Remove Vertex only makes sense on intermediate vertices.
  if (kind === "mleader-vertex") {
    entries.push({
      id: "remove-vertex",
      labelKey: "mleader.menu.remove_vertex",
    });
  }
  // Stretch closes the menu — any grip kind can be stretched.
  entries.push({ id: "stretch", labelKey: "mleader.menu.stretch" });
  return entries;
}

export function MleaderGripContextMenu(
  props: MleaderGripContextMenuProps,
): React.ReactElement {
  const t = useT();
  const { screen, gripKind, onPick, onCancel } = props;

  React.useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onCancel();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onCancel]);

  const entries = resolveMenuEntries(gripKind);
  const PIN_OFFSET = 4;
  const menuWidth = 200;
  const menuMaxHeight = 280;
  const viewportWidth =
    typeof window !== "undefined" ? window.innerWidth : 1024;
  const viewportHeight =
    typeof window !== "undefined" ? window.innerHeight : 768;
  let left = screen.x + PIN_OFFSET;
  let top = screen.y + PIN_OFFSET;
  if (left + menuWidth > viewportWidth) left = viewportWidth - menuWidth - 4;
  if (top + menuMaxHeight > viewportHeight)
    top = Math.max(viewportHeight - menuMaxHeight - 4, 4);

  return (
    <div
      data-testid="mleader-grip-context-menu-overlay"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 100,
        pointerEvents: "auto",
      }}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onCancel();
      }}
      onContextMenu={(event) => {
        event.preventDefault();
        onCancel();
      }}
    >
      <div
        data-testid="mleader-grip-context-menu"
        role="menu"
        style={{
          position: "absolute",
          left,
          top,
          minWidth: menuWidth,
          maxHeight: menuMaxHeight,
          overflowY: "auto",
          background: "#262629",
          border: "1px solid #3a3a3e",
          borderRadius: 4,
          boxShadow: "0 6px 18px rgba(0, 0, 0, 0.45)",
          padding: "4px 0",
          fontFamily: "Inter, sans-serif",
          fontSize: 12,
          color: "#f0f0f1",
        }}
      >
        {entries.map((entry) => (
          <button
            key={entry.id}
            type="button"
            role="menuitem"
            data-testid={`mleader-menu-${entry.id}`}
            onClick={() => onPick(entry.id)}
            style={{
              display: "block",
              width: "100%",
              padding: "6px 12px",
              textAlign: "left",
              background: "transparent",
              border: "none",
              color: "inherit",
              cursor: "pointer",
              font: "inherit",
            }}
          >
            {t(entry.labelKey)}
          </button>
        ))}
      </div>
    </div>
  );
}
